import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { CurrencyIcon, ICON_OPTIONS } from "../lib/currencyDisplay";

const CATEGORY_LABELS = [
  ["all", "All"],
  ["general", "General"],
  ["currency", "Currency"],
  ["rpg", "RPG"],
  ["chaos", "Chaos"],
  ["alerts", "Alerts"],
  ["media", "Media"],
  ["holiday", "Holiday"],
  ["game", "Game"],
  ["utility", "Utility"],
];

const ICON_CATEGORY = {
  Carrot: "currency",
  Coins: "currency",
  Ticket: "currency",
  CircleDollarSign: "currency",
  BadgeDollarSign: "currency",
  HandCoins: "currency",
  Landmark: "currency",
  BadgeAlert: "alerts",
  BadgeCheck: "alerts",
  Ban: "utility",
  CircleAlert: "alerts",
  CircleCheck: "utility",
  CirclePlay: "media",
  CircleStop: "media",
  Gift: "holiday",
  Candy: "holiday",
  PartyPopper: "alerts",
  Trophy: "game",
  Medal: "game",
  Crown: "rpg",
  Badge: "general",
  Eye: "chaos",
  EyeOff: "chaos",
  ScanEye: "chaos",
  Glasses: "general",
  Projector: "media",
  Scan: "utility",
  ScanLine: "utility",
  ScanFace: "utility",
  Box: "utility",
  Boxes: "utility",
  BookOpen: "general",
  Bookmark: "utility",
  Gem: "currency",
  Star: "general",
  Sparkles: "general",
  Heart: "general",
  Smile: "general",
  Laugh: "general",
  Drama: "chaos",
  Zap: "chaos",
  Bomb: "chaos",
  Flame: "chaos",
  CloudLightning: "chaos",
  WandSparkles: "chaos",
  Swords: "rpg",
  Shield: "rpg",
  ShieldAlert: "rpg",
  Target: "game",
  Crosshair: "game",
  MousePointerBan: "utility",
  Flag: "utility",
  Map: "rpg",
  Compass: "rpg",
  Radar: "utility",
  QrCode: "utility",
  Anchor: "general",
  Castle: "rpg",
  Hammer: "rpg",
  Key: "rpg",
  Scroll: "rpg",
  Archive: "utility",
  Package: "utility",
  PackageOpen: "utility",
  Upload: "utility",
  Download: "utility",
  Dice6: "game",
  Dices: "game",
  Gamepad2: "game",
  Joystick: "game",
  PersonStanding: "game",
  Footprints: "game",
  Music: "media",
  Music2: "media",
  Headphones: "media",
  Mic: "media",
  Volume2: "media",
  Radio: "media",
  Drum: "media",
  Disc3: "media",
  Video: "media",
  Film: "media",
  Clapperboard: "media",
  Camera: "media",
  Aperture: "media",
  Image: "media",
  Images: "media",
  Tv: "media",
  MonitorPlay: "media",
  SquarePlay: "media",
  FastForward: "media",
  Megaphone: "alerts",
  Bell: "alerts",
  MessageCircle: "alerts",
  Bot: "utility",
  Brain: "utility",
  Cpu: "utility",
  Code: "utility",
  Cog: "utility",
  Command: "utility",
  Cable: "utility",
  Webhook: "utility",
  SlidersHorizontal: "utility",
  Layers: "utility",
  Gauge: "utility",
  LoaderCircle: "utility",
  Lock: "utility",
  LockOpen: "utility",
  UserCog: "utility",
  Rocket: "chaos",
  Lightbulb: "utility",
  AlarmClock: "utility",
  Clock: "utility",
  Hourglass: "utility",
  Timer: "utility",
  Palette: "utility",
  PaintBucket: "utility",
  Brush: "utility",
  BrushCleaning: "utility",
  Droplets: "chaos",
  Waves: "chaos",
  Cloud: "chaos",
  CloudRain: "chaos",
  SprayCan: "chaos",
  ShowerHead: "chaos",
  Syringe: "utility",
  Eraser: "utility",
  HandHeart: "general",
  Beer: "holiday",
  Martini: "holiday",
  Wine: "holiday",
  Coffee: "general",
  GlassWater: "general",
  CupSoda: "holiday",
  Milk: "general",
  Plane: "general",
  Briefcase: "utility",
  Rabbit: "general",
};

export default function IconPicker({ label = "Icon", value, color = "hsl(var(--primary))", onChange, test = "icon-picker" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const iconOptions = useMemo(() => ICON_OPTIONS.filter((option) => Array.isArray(option) && option[0] && option[1]), []);
  const selected = iconOptions.find(([id]) => id === value);
  const selectedLabel = selected?.[1] || value || "Icon";

  const filteredIcons = useMemo(() => {
    const q = query.trim().toLowerCase();
    return iconOptions.filter((option) => {
      const [id, name] = option;
      const iconCategory = ICON_CATEGORY[id] || "general";
      const matchesCategory = category === "all" || iconCategory === category;
      const matchesQuery = !q || String(id).toLowerCase().includes(q) || String(name).toLowerCase().includes(q);
      return matchesCategory && matchesQuery;
    });
  }, [category, iconOptions, query]);

  const choose = (id) => {
    onChange?.(id);
    setOpen(false);
  };

  return (
    <div data-testid={test}>
      <div className="label-eyebrow mb-1.5">{label}</div>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] flex items-center justify-center">
          <CurrencyIcon currency={{ icon: value, color }} className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="text-sm truncate">{selectedLabel}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono truncate">{value || "none"}</div>
        </div>
        <button type="button" onClick={() => setOpen(true)} className="ml-auto border border-[hsl(var(--border))] text-sm px-3 py-2 rounded hover:border-[hsl(var(--primary))]">
          Choose Icon
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="surface w-full max-w-3xl max-h-[86vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-[hsl(var(--border))] flex items-center justify-between gap-3">
              <div>
                <div className="label-eyebrow">Icon repository</div>
                <h2 className="text-lg font-semibold tracking-tight">Choose Icon</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-2 text-[hsl(var(--muted-foreground))] hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 border-b border-[hsl(var(--border))] space-y-3">
              <div className="surface flex items-center gap-2 px-3 py-2">
                <Search className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search icons..." className="bg-transparent flex-1 text-sm focus:outline-none" />
              </div>
              <div className="flex flex-wrap gap-2">
                {CATEGORY_LABELS.map(([id, name]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setCategory(id)}
                    className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded border ${category === id ? "border-[hsl(var(--primary))] text-white bg-[hsl(var(--primary))]/10" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 overflow-y-auto">
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                {filteredIcons.map(([id, name]) => (
                  <button
                    type="button"
                    key={id}
                    onClick={() => choose(id)}
                    title={name}
                    className={`h-14 rounded border flex flex-col items-center justify-center gap-1 ${value === id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10" : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]"}`}
                  >
                    <CurrencyIcon currency={{ icon: id, color }} className="w-4 h-4" />
                    <span className="text-[10px] text-[hsl(var(--muted-foreground))] truncate max-w-[4.5rem]">{name}</span>
                  </button>
                ))}
              </div>
              {filteredIcons.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))] text-center py-10">No icons match.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
